import { useMemo } from "react";

import {
  getFixedProperties,
  getOrderedProperties,
} from "src/entities/entityView/utils";
import { EntitySchemaResource } from "src/entities/queries";
import { usePersistedColumnOrder } from "src/hooks/useColumnOrder";
import { useWorkspaceContext } from "src/router/routerContextHooks";

const STORAGE_KEY = "entityColumnOrder";

type UseEntityColumnOrderReturn = {
  orderedPropertyKeys: string[];
  hasCustomOrder: boolean;
  setOrder: (keys: string[]) => void;
  resetOrder: () => void;
};

/**
 * The entity schema is the single source of the global order, so a property
 * added to it after an override was saved lands next to the neighbor it
 * follows there, and a property removed from it is dropped.
 */
export const reconcileEntityColumnOrder = (
  defaultOrder: string[],
  storedOrder: string[],
): string[] => {
  const defaultIds = new Set(defaultOrder);
  const storedIds = new Set(storedOrder);
  const keptIds = storedOrder.filter((id) => defaultIds.has(id));
  const missingIds = defaultOrder.filter((id) => !storedIds.has(id));

  return missingIds.reduce((orderedIds, missingId) => {
    const precedingId = defaultOrder
      .slice(0, defaultOrder.indexOf(missingId))
      .reverse()
      .find((candidate) => orderedIds.includes(candidate));
    const insertAt = precedingId ? orderedIds.indexOf(precedingId) + 1 : 0;
    return [
      ...orderedIds.slice(0, insertAt),
      missingId,
      ...orderedIds.slice(insertAt),
    ];
  }, keptIds);
};

/**
 * Personal, per-browser column order for the entities table. Overlays a
 * localStorage override on top of the global `_order` defined in the entity
 * schema; falls back to the global order when no override is stored.
 */
export const useEntityColumnOrder = (
  entitySchema: EntitySchemaResource,
): UseEntityColumnOrderReturn => {
  const { workspace } = useWorkspaceContext();

  // ID, primary, and secondary property are pinned first and can't be
  // reordered, so only the remaining properties participate in the override
  const defaultOrder = useMemo(() => {
    const fixedKeys = new Set(getFixedProperties(entitySchema));
    return getOrderedProperties(entitySchema).filter(
      (propertyKey) => !fixedKeys.has(propertyKey),
    );
  }, [entitySchema]);

  const { orderedColumnIds, hasCustomOrder, setOrder, resetOrder } =
    usePersistedColumnOrder({
      storageKey: STORAGE_KEY,
      identifier: `${workspace.id}|${entitySchema._id}`,
      defaultOrder,
      reconcile: reconcileEntityColumnOrder,
    });

  return {
    orderedPropertyKeys: orderedColumnIds,
    hasCustomOrder,
    setOrder,
    resetOrder,
  };
};
